import React, { Suspense, lazy } from "react";
import ReactDOM from "react-dom/client";
import { App as AppWrapper, ConfigProvider } from "antd";
import { Provider } from "react-redux";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";

// Store
import store from "./store";

// Helpers
import { ErrorFallbackPage, SiteLoader } from "./helpers/utils";

// ToastHandler
import ToastHandler from "./helpers/ToastHandler";

// Translation
import "./helpers/i18n";

// Header CSS
import "./Components/CSS/header.css";

// CSS
import "./Assets/CSS/custom.css";
import "./Assets/CSS/style.css";

// Auth CSS
import "./Auth/CSS/style.css";

// Component CSS
import "./Components/CSS/style.css";

// View CSS
import "./View/CSS/style.css";

// Swiper Js
import "swiper/css";
import "swiper/css/navigation";
import { navigationRoute } from "./Components/DummyData";

const root = ReactDOM.createRoot(document.getElementById("root"));

// Auth Pages
const AuthIndex = lazy(() => import("./Auth/AuthIndex"));
const Login = lazy(() => import("./Auth/Pages/Login"));

// Admins Pages
const ViewIndex = lazy(() => import("./View/ViewIndex"));
const Profile = lazy(() => import("./View/Pages/Profile"));
const Dashboard = lazy(() => import("./View/Pages/Dashboard"));
const Users = lazy(() => import("./View/Pages/Users"));
const Organization = lazy(() => import("./View/Pages/Organization"));
const OrganizationID = lazy(() => import("./View/Pages/OrganizationID"));
const UserDetail = lazy(() => import("./View/Pages/UserDetail"));
const AIUsage = lazy(() => import("./View/Pages/AIUsage"));
const AISettings = lazy(() => import("./View/Pages/AI/AISettings"));
const AICredits = lazy(() => import("./View/Pages/AI/AICredits"));

const Analytics = lazy(() => import("./View/Pages/Analytics"));
const Content = lazy(() => import("./View/Pages/Content"));
const Applications = lazy(() => import("./View/Pages/Applications/List"));
const ApplicationDetail = lazy(() =>
  import("./View/Pages/Applications/Detail")
);
const ManageUserType = lazy(() => import("./View/Pages/ManageUserType/List"));
const AddEditUserType = lazy(() =>
  import("./View/Pages/ManageUserType/AddEdit")
);
const NotificationTemplate = lazy(() =>
  import("./View/Pages/NotificationTemplate")
);
const EmailTemplate = lazy(() => import("./View/Pages/EmailTemplate"));
const DocumentTemplate = lazy(() => import("./View/Pages/DocumentTemplate"));
const BetaProgramPage = lazy(() => import("./View/Pages/BetaProgram"));
const FAQ = lazy(() => import("./View/Pages/Faq"));
const Support = lazy(() => import("./View/Pages/Support"));
const SupportUser = lazy(() => import("./View/Pages/SupportTeam"));
const SupportCategory = lazy(() => import("./View/Pages/SupportCategory"));
const ABTesting = lazy(() => import("./View/Pages/ABTest/ABTesting"));

const Subscription = lazy(() =>
  import("./View/Pages/Subscription/Subscription")
);
const ManagePlan = lazy(() => import("./View/Pages/Subscription/ManagePlan"));
const EditManagePlan = lazy(() =>
  import("./View/Pages/Subscription/EditManagePlan")
);
const Transaction = lazy(() => import("./View/Pages/Subscription/Transaction"));
const ImportExperience = lazy(() => import("./View/Pages/ImportExperience"));

// Cosmos Pages
const CosmosWidgets = lazy(() => import("./View/Pages/Cosmos/widgets"));
const AddUpdateWidgets = lazy(() =>
  import("./View/Pages/Cosmos/addUpdateWidgets")
);
const WidgetDataBindPreview = lazy(() =>
  import("./View/Pages/Cosmos/widgetDataBindPreview")
);
const CosmosDynamicPage = lazy(() => import("./View/Pages/Cosmos/pages"));
const AddUpdatePages = lazy(() => import("./View/Pages/Cosmos/addUpdatePages"));
const CosmosPagePreview = lazy(() =>
  import("./View/Pages/Cosmos/cosmosPagePreview")
);
const CosmosThemes = lazy(() => import("./View/Pages/Cosmos/cosmosTheme"));
const CosmosThemeEdit = lazy(() =>
  import("./View/Pages/Cosmos/cosmosThemeEdit")
);

const NewsLetter = lazy(() => import("./View/Pages/NewsLetter"));

/** Error Page */
const PageNotFound = lazy(() => import("./Components/Pages/Error"));

export const alias = process.env.REACT_APP_ALIAS || "";

root.render(
  <React.StrictMode>
    <ConfigProvider
      theme={{
        token: {
          fontFamily: "Averta",
          colorPrimary: "#3961e5",
        },
      }}
    >
      <AppWrapper>
        <Provider store={store}>
          <BrowserRouter basename={alias}>
            <Suspense fallback={<SiteLoader />}>
              <ErrorBoundary FallbackComponent={ErrorFallbackPage}>
                {/* Route List */}
                <Routes>
                  {/* Auth */}
                  <Route path="/auth" element={<AuthIndex />}>
                    <Route path="login" element={<Login />} />
                  </Route>
                  {/* Auth */}

                  <Route path="/" element={<AuthIndex />}>
                    <Route path="" element={<Login />} />
                  </Route>

                  <Route
                    exact
                    path="cosmos/page-preview/:id"
                    element={<CosmosPagePreview />}
                  />
                  <Route
                    exact
                    path="cosmos/page-preview/:id/:expId"
                    element={<CosmosPagePreview />}
                  />
                  <Route
                    exact
                    path="cosmos/draft-preview/:id"
                    element={<CosmosPagePreview />}
                  />
                  <Route
                    exact
                    path="cosmos/draft-preview/:id/:expId"
                    element={<CosmosPagePreview />}
                  />

                  {/* Admin */}
                  <Route
                    path="/admin"
                    element={<ViewIndex roleName={""} data={navigationRoute} />}
                  >
                    <Route exact path="profile" element={<Profile />} />
                    <Route exact path="dashboard" element={<Dashboard />} />
                    <Route exact path="users" element={<Users />} />
                    <Route exact path="organization">
                      <Route path="" element={<Organization />} />
                      <Route
                        path=":organizationId/detail"
                        element={<OrganizationID />}
                      />
                    </Route>

                    <Route
                      exact
                      path="users/:userId/user-detail"
                      element={<UserDetail />}
                    />
                    {/* <Route exact path="application" element={<Applications />} /> */}
                    <Route exact path="application">
                      <Route path="" element={<Applications />} />
                      <Route path=":id" element={<ApplicationDetail />} />
                    </Route>

                    <Route exact path="ai-usage" element={<AIUsage />} />
                    <Route exact path="ab-testing" element={<ABTesting />} />
                    <Route exact path="ai-settings" element={<AISettings />} />
                    <Route exact path="ai-credits" element={<AICredits />} />

                    <Route exact path="user-type">
                      <Route path="" element={<ManageUserType />} />
                      <Route path="add" element={<AddEditUserType />} />
                      <Route path=":id/edit" element={<AddEditUserType />} />
                    </Route>
                    <Route exact path="analytics" element={<Analytics />} />
                    <Route exact path="content" element={<Content />} />
                    <Route
                      exact
                      path="notification-template"
                      element={<NotificationTemplate />}
                    />
                    <Route
                      exact
                      path="email-template"
                      element={<EmailTemplate />}
                    />
                    <Route
                      exact
                      path="document-template"
                      element={<DocumentTemplate />}
                    />
                    <Route
                      exact
                      path="beta-program"
                      element={<BetaProgramPage />}
                    />

                    <Route exact path="faq" element={<FAQ />} />
                    <Route exact path="support">
                      <Route path="" element={<Support />} />
                      <Route path="category" element={<SupportCategory />} />
                    </Route>
                    <Route
                      exact
                      path="subscription"
                      element={<Subscription />}
                    />

                    <Route
                      exact
                      path="support-for-user"
                      element={<SupportUser />}
                    />

                    <Route exact path="manage-plan">
                      <Route path="" element={<ManagePlan />} />
                      <Route path="add" element={<EditManagePlan />} />
                      <Route path=":id/edit" element={<EditManagePlan />} />
                    </Route>
                    <Route exact path="transaction" element={<Transaction />} />
                    <Route exact path="transaction" element={<Transaction />} />
                    <Route
                      exact
                      path="import-experience-history"
                      element={<ImportExperience />}
                    />

                    <Route exact path="newsletter" element={<NewsLetter />} />

                    <Route path="cosmos">
                      {/* Widgets */}
                      <Route path="widgets">
                        <Route path="" element={<CosmosWidgets />} />
                        <Route path=":id" element={<AddUpdateWidgets />} />
                      </Route>

                      <Route
                        path="widgets-preview/:id"
                        element={<WidgetDataBindPreview />}
                      />
                      <Route
                        exact
                        path="cosmos/pages"
                        element={<CosmosDynamicPage />}
                      />
                      <Route
                        exact
                        path="cosmos/pages/:id"
                        element={<AddUpdatePages />}
                      />

                      {/* Pages */}
                      <Route path="pages">
                        <Route path="" element={<CosmosDynamicPage />} />
                        <Route path=":id" element={<AddUpdatePages />} />
                      </Route>
                      <Route
                        path="page-preview/:id"
                        element={<CosmosPagePreview />}
                      />

                      {/* Themes */}
                      <Route path="theme">
                        <Route path="" element={<CosmosThemes />} />
                        <Route path=":id" element={<CosmosThemeEdit />} />
                      </Route>
                    </Route>

                    <Route path="*" element={<PageNotFound />} />
                  </Route>
                  {/* Admin */}

                  <Route path="*" element={<PageNotFound />} />

                  {/* Comman */}
                </Routes>
                {/* Route List End */}
              </ErrorBoundary>
            </Suspense>
          </BrowserRouter>
          <ToastHandler />
        </Provider>
      </AppWrapper>
    </ConfigProvider>
  </React.StrictMode>
);
